import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select } from '../ui/newSelect';
import BrandSelect from '../ui/BrandSelect';
import { adminToolbarSelect } from '../utils/ReactSelectStyles';
import { Alert, AlertDescription } from '../ui/alert';
import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip';
import { CircleHelp } from 'lucide-react';
import { Actions as CONSULTANT_ACTIONS } from '../../store/actions/consultantClientsActions';
import { Actions as AGREEMENTS } from '../../store/actions/agreementsActions';
import { toggleAlertMessage } from '../../store/action';
import {
  BILLING_CURRENCY_OPTIONS,
  buildAgreementLineItemsPayload,
  buildIndexationPolicyPayload,
  defaultSubscriptionLine,
  flattenStripeProductsToPriceOptions,
  symbolToStripeCurrency,
} from '../utils/agreementLineItemHelpers';
import {
  findMatchingTier,
  isTieredPrice,
  resolveBillingUnit,
  formatMajorMoney,
} from '../utils/stripeBanding';
import { recurringToBillingFrequency, stripeCurrencyToSymbol } from '../utils/stripeCurrency';
import { buildAgreementCountryPayload } from '../Modals/agreementsUtils';
import { friendlyConsultantClientError } from '../utils/consultantClientUtils';

const FieldHelp = ({ label, help }) => (
  <div className='flex items-center gap-1.5'>
    <Label>{label}</Label>
    <Tooltip>
      <TooltipTrigger asChild>
        <Button type='button' variant='ghost' size='icon' className='h-6 w-6 text-muted-foreground' aria-label={`About ${label}`}>
          <CircleHelp size={14} strokeWidth={1.5} />
        </Button>
      </TooltipTrigger>
      <TooltipContent className='bg-popover text-popover-foreground border-border max-w-xs'>
        {help}
      </TooltipContent>
    </Tooltip>
  </div>
);

const INITIAL_FORM = {
  billingRelationship: 'consultant_billed',
  customerOrganisationName: '',
  customerEmail: '',
  customerFirstName: '',
  customerLastName: '',
  customerRegisteredCountry: '',
  billingCurrency: '£',
  planType: 'per_report',
  stripePriceId: '',
  stripeProductId: '',
  unitAmount: '',
  bandingQuantity: '',
  perReportFeeVal: '',
  payMode: 'monthly_cycle',
  termMonths: 12,
  autoRenew: true,
  billingFrequency: 'monthly',
  directPayMode: 'invoice',
  term: { type: 'committed', lengthMonths: 12, noticePeriodDays: 60, startDate: '' },
  indexationPolicy: { kind: 'none' },
  lineItems: [defaultSubscriptionLine()],
};

const AddConsultantClientWizard = ({
  open,
  onClose,
  onSuccess,
  countries,
  consultantParentId,
  createConsultantClient,
  getDirectSubscriptionProducts,
  getStripeProducts,
  getMyAgreements,
  directProducts,
  directProductsLoading,
  agreementProductsByCurrency,
  showAlert,
}) => {
  const [form, setForm] = useState(INITIAL_FORM);
  const [saving, setSaving] = useState(false);
  const [step, setStep] = useState(1);
  const [directFlowDisabled, setDirectFlowDisabled] = useState(false);
  const [masterPerReportFeeVal, setMasterPerReportFeeVal] = useState(null);

  const isDirect = form.billingRelationship === 'direct_billed';
  const contractIso = symbolToStripeCurrency(form.billingCurrency);

  useEffect(() => {
    if (!open) {
      setForm(INITIAL_FORM);
      setStep(1);
      setDirectFlowDisabled(false);
      setMasterPerReportFeeVal(null);
      return;
    }
    if (consultantParentId) {
      getMyAgreements(consultantParentId).then((data) => {
        const agreements = data?.agreements || [];
        const master = agreements.find(
          (a) => a.type === 'Consultant' && (a.status === 'complete' || a.active === true),
        );
        const fee = master?.perReportFeeVal;
        if (fee != null && fee !== '' && !Number.isNaN(Number(fee))) {
          setMasterPerReportFeeVal(Number(fee));
          setForm((f) => ({
            ...f,
            planType: 'per_report',
            perReportFeeVal: f.perReportFeeVal !== '' ? f.perReportFeeVal : String(Number(fee)),
          }));
        }
      });
    }
    if (contractIso) {
      if (isDirect) {
        getDirectSubscriptionProducts(contractIso).then((r) => {
          if (r.status === 404 || r.error === 'agreements_flow_disabled') setDirectFlowDisabled(true);
        });
      } else {
        getStripeProducts({ currency: contractIso, limit: 50 });
      }
    }
  }, [open, isDirect, contractIso, consultantParentId, getDirectSubscriptionProducts, getStripeProducts, getMyAgreements]);

  const directPriceOptions = useMemo(() => {
    const products = (directProducts?.products || []).filter((p) => !p?.metadata?.cyf_addon_product_id);
    return flattenStripeProductsToPriceOptions(products).filter((o) => !o.price?.metadata?.cyf_addon_product_id);
  }, [directProducts]);

  const consultantPriceOptions = useMemo(() => {
    const iso = symbolToStripeCurrency(form.billingCurrency);
    const products = agreementProductsByCurrency[iso] || [];
    return flattenStripeProductsToPriceOptions(products);
  }, [agreementProductsByCurrency, form.billingCurrency]);

  const update = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const validate = () => {
    if (!form.customerOrganisationName.trim()) return 'Enter the client organisation name.';
    if (!form.customerEmail.trim()) return 'Enter the client contact email.';
    if (!form.customerFirstName.trim() || !form.customerLastName.trim()) return 'Enter the client contact name.';
    if (isDirect) {
      if (directFlowDisabled) return 'Direct client billing is not enabled.';
      const line = form.lineItems[0];
      if (!line?.stripePriceId) return 'Pick a subscription plan for the client.';
      const picked = directPriceOptions.find((o) => o.price?.id === line.stripePriceId);
      if (picked && isTieredPrice(picked.price)) {
        const q = Number(line.bandingQuantity);
        if (!Number.isFinite(q) || q <= 0) return 'Enter a banding quantity for the selected tiered price.';
      }
      if (!form.directPayMode) return 'Select how the client will pay.';
    } else if (form.planType === 'client_subscription') {
      if (!form.stripePriceId) return 'Pick a subscription plan.';
      if (form.unitAmount === '') return 'Enter the unit amount in major currency units.';
    } else if (form.planType === 'per_report') {
      if (form.perReportFeeVal === '' || Number(form.perReportFeeVal) <= 0) return 'Enter the per-report fee.';
    }
    return null;
  };

  const onSubmit = async () => {
    const err = validate();
    if (err) {
      showAlert(true, err, 'error', 5000);
      return;
    }

    setSaving(true);
    let body = {
      billingRelationship: form.billingRelationship,
      customerOrganisationName: form.customerOrganisationName.trim(),
      customerEmail: form.customerEmail.trim(),
      customerFirstName: form.customerFirstName.trim(),
      customerLastName: form.customerLastName.trim(),
      billingCurrency: form.billingCurrency,
      ...buildAgreementCountryPayload({
        incorporationCountryId: form.customerRegisteredCountry,
        billingCountryId: form.customerRegisteredCountry,
        countries,
      }),
    };

    if (isDirect) {
      body = {
        ...body,
        payMode: form.directPayMode,
        term: {
          ...form.term,
          lengthMonths: Number(form.term.lengthMonths) || 12,
          noticePeriodDays: Number(form.term.noticePeriodDays) || 0,
        },
        indexationPolicy: buildIndexationPolicyPayload(form.indexationPolicy),
        lineItems: buildAgreementLineItemsPayload(form.lineItems, directPriceOptions),
      };
    } else {
      body = {
        ...body,
        planType: form.planType,
        payMode: form.payMode,
        termMonths: Number(form.termMonths) || 12,
        autoRenew: !!form.autoRenew,
        billingFrequency: form.billingFrequency,
      };
      if (form.planType === 'client_subscription') {
        body.stripePriceId = form.stripePriceId;
        body.unitAmount = Number(form.unitAmount) || 0;
      } else {
        body.perReportFeeVal = Number(form.perReportFeeVal);
      }
    }

    const result = await createConsultantClient(body);
    setSaving(false);

    if (result.ok) {
      const msg = isDirect
        ? `Invitation sent to ${form.customerEmail}. The client must accept the order form and complete payment before anyone can add facilities or usage. Resend from Client Portfolio if needed.`
        : 'Client created. Activate it from Client Portfolio when you are ready — facilities and usage stay locked until then.';
      showAlert(true, msg, 'success', null);
      if (result.data?.agreementUrl) window.open(result.data.agreementUrl, '_blank', 'noopener');
      onSuccess?.(result.data);
    } else {
      showAlert(true, friendlyConsultantClientError(result.error || result.message), 'error', 6000);
    }
  };

  const countryOptions = (countries || []).map((c) => ({ value: c.id, label: c.name }));

  const onPickConsultantPrice = (opt) => {
    if (!opt) {
      setForm((f) => ({
        ...f,
        stripePriceId: '',
        stripeProductId: '',
        unitAmount: '',
        bandingQuantity: '',
      }));
      return;
    }
    const { product, price } = opt;
    setForm((f) => ({
      ...f,
      stripeProductId: product.id,
      stripePriceId: price.id,
      unitAmount: isTieredPrice(price) ? '' : price.unit_amount != null ? price.unit_amount / 100 : '',
      bandingQuantity: '',
    }));
  };

  const setLine = (i, key, value) => {
    setForm((f) => ({
      ...f,
      lineItems: f.lineItems.map((row, j) => (j === i ? { ...row, [key]: value } : row)),
    }));
  };

  const onPickDirectPrice = (i, opt) => {
    if (!opt) {
      setLine(i, 'stripePriceId', '');
      setLine(i, 'stripeProductId', '');
      setLine(i, 'currency', '');
      setLine(i, 'bandingQuantity', '');
      return;
    }
    const { product, price } = opt;
    setForm((f) => ({
      ...f,
      lineItems: f.lineItems.map((row, j) =>
        j === i
          ? {
              ...row,
              stripeProductId: product.id,
              stripePriceId: price.id,
              currency: price.currency,
              billingFrequency: recurringToBillingFrequency(price.recurring),
              unitAmount: isTieredPrice(price) ? '' : price.unit_amount != null ? price.unit_amount / 100 : '',
              bandingQuantity: '',
            }
          : row,
      ),
    }));
  };

  const directLine = form.lineItems[0];
  const directPicked = directPriceOptions.find((o) => o.price?.id === directLine?.stripePriceId);
  const directBillingUnit = directPicked ? resolveBillingUnit(directPicked.price, directPicked.product) : null;
  const matchedDirectTier =
    directPicked && isTieredPrice(directPicked.price) && directLine?.bandingQuantity
      ? findMatchingTier(directPicked.price.tiers, Number(directLine.bandingQuantity))
      : null;
  const matchedDirectTierMinor =
    typeof matchedDirectTier?.flatAmount === 'number'
      ? matchedDirectTier.flatAmount
      : matchedDirectTier?.unitAmount;
  const bandingHelp =
    directBillingUnit?.unit === 'revenue'
      ? "Enter the client's annual revenue in this plan's currency (whole number, no commas). This picks the price band they will be billed at."
      : directBillingUnit?.unit === 'fte'
        ? "Enter the client's number of FTEs (whole number). This picks the price band they will be billed at."
        : "Enter the client's banding quantity (for example annual revenue or FTE count). This picks the price band they will be billed at. The matched band amount is shown below.";

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className='bg-card text-card-foreground border-border max-w-2xl max-h-[90vh] overflow-y-auto'>
        <DialogHeader>
          <DialogTitle>Add client</DialogTitle>
        </DialogHeader>

        {step === 1 && (
          <div className='space-y-4 p-4'>
            <p className='text-sm text-muted-foreground'>Choose who pays for this client&apos;s platform access.</p>
            <div className='grid grid-cols-1 md:grid-cols-2 gap-3'>
              <Button
                type='button'
                variant={form.billingRelationship === 'consultant_billed' ? 'default' : 'outline'}
                className='h-auto py-4 flex flex-col items-start'
                onClick={() => update('billingRelationship', 'consultant_billed')}>
                <span className='font-semibold'>Billed to you</span>
                <span className='text-xs opacity-80'>You pay — activate when ready</span>
              </Button>
              <Button
                type='button'
                variant={form.billingRelationship === 'direct_billed' ? 'default' : 'outline'}
                className='h-auto py-4 flex flex-col items-start'
                disabled={directFlowDisabled}
                onClick={() => update('billingRelationship', 'direct_billed')}>
                <span className='font-semibold'>Client subscription</span>
                <span className='text-xs opacity-80'>Client pays via order form</span>
              </Button>
            </div>
            {form.billingRelationship === 'consultant_billed' && (
              <Alert variant='info'>
                <AlertDescription className='text-xs text-foreground'>
                  You create the organisation now, then <strong>Activate</strong> it from Client Portfolio when you are
                  ready. Until then, facilities and usage stay locked.
                </AlertDescription>
              </Alert>
            )}
            {form.billingRelationship === 'direct_billed' && !directFlowDisabled && (
              <Alert variant='info'>
                <AlertDescription className='text-xs text-foreground'>
                  An order form is emailed to the client. The organisation is created as a Direct User with no active
                  subscription until they accept and pay. Facilities and usage stay locked until then — there is no
                  automatic reminder later, so use Client Portfolio to resend the invitation if needed.
                </AlertDescription>
              </Alert>
            )}
            {directFlowDisabled && (
              <Alert>
                <AlertDescription className='text-xs'>
                  Direct client billing is not enabled on this environment. Use &quot;Billed to you&quot; instead.
                </AlertDescription>
              </Alert>
            )}
          </div>
        )}

        {step === 2 && (
          <div className='space-y-4 p-4'>
            <div className='grid grid-cols-1 md:grid-cols-2 gap-3'>
              <div className='md:col-span-2'>
                <Label>Organisation name *</Label>
                <Input
                  value={form.customerOrganisationName}
                  onChange={(e) => update('customerOrganisationName', e.target.value)}
                />
              </div>
              <div>
                <Label>First name *</Label>
                <Input value={form.customerFirstName} onChange={(e) => update('customerFirstName', e.target.value)} />
              </div>
              <div>
                <Label>Last name *</Label>
                <Input value={form.customerLastName} onChange={(e) => update('customerLastName', e.target.value)} />
              </div>
              <div className='md:col-span-2'>
                <Label>Email *</Label>
                <Input type='email' value={form.customerEmail} onChange={(e) => update('customerEmail', e.target.value)} />
              </div>
              <div>
                <Label>Country</Label>
                <Select
                  value={form.customerRegisteredCountry}
                  onValueChange={(v) => update('customerRegisteredCountry', v)}
                  placeholder='Select country'
                  options={countryOptions}
                  searchable
                />
              </div>
              <div>
                <Label>Billing currency</Label>
                <Select
                  value={form.billingCurrency}
                  onValueChange={(v) => update('billingCurrency', v)}
                  options={BILLING_CURRENCY_OPTIONS.map((c) => ({ value: c, label: c }))}
                />
              </div>
            </div>

            {!isDirect && (
              <div className='space-y-3 border-t border-border pt-4'>
                <div>
                  <Label>Plan type</Label>
                  <Select
                    value={form.planType}
                    onValueChange={(v) => update('planType', v)}
                    options={[
                      { value: 'per_report', label: 'Per report' },
                      { value: 'client_subscription', label: 'Client subscription' },
                    ]}
                  />
                </div>
                {form.planType === 'client_subscription' && (
                  <>
                    <div>
                      <Label>Stripe plan *</Label>
                      <BrandSelect
                        options={consultantPriceOptions}
                        value={consultantPriceOptions.find((o) => o.value === form.stripePriceId) || null}
                        onChange={onPickConsultantPrice}
                        styles={adminToolbarSelect}
                        isSearchable
                        placeholder='Search plans…'
                      />
                    </div>
                    <div>
                      <Label>Unit amount (major units) *</Label>
                      <Input
                        type='number'
                        min='0'
                        step='0.01'
                        value={form.unitAmount}
                        onChange={(e) => update('unitAmount', e.target.value)}
                      />
                    </div>
                  </>
                )}
                {form.planType === 'per_report' && (
                  <div>
                    <Label>Per-report fee *</Label>
                    <Input
                      type='number'
                      min='0'
                      step='0.01'
                      value={form.perReportFeeVal}
                      onChange={(e) => update('perReportFeeVal', e.target.value)}
                    />
                    {masterPerReportFeeVal != null && (
                      <p className='text-xs text-muted-foreground mt-1'>
                        Pre-filled from your consultant master agreement ({form.billingCurrency}
                        {masterPerReportFeeVal} per report). Commitment end date is set by CYF admin.
                      </p>
                    )}
                  </div>
                )}
                <div className='grid grid-cols-2 gap-3'>
                  <div>
                    <Label>Pay mode</Label>
                    <Select
                      value={form.payMode}
                      onValueChange={(v) => update('payMode', v)}
                      options={[
                        { value: 'monthly_cycle', label: 'Monthly cycle' },
                        { value: 'pay_in_full', label: 'Pay in full' },
                      ]}
                    />
                  </div>
                  <div>
                    <Label>Term (months)</Label>
                    <Input
                      type='number'
                      min='1'
                      value={form.termMonths}
                      onChange={(e) => update('termMonths', e.target.value)}
                    />
                  </div>
                </div>
              </div>
            )}

            {isDirect && (
              <div className='space-y-3 border-t border-border pt-4'>
                <div>
                  <Label>Subscription plan *</Label>
                  <BrandSelect
                    options={directPriceOptions}
                    value={directPriceOptions.find((o) => o.value === directLine?.stripePriceId) || null}
                    onChange={(opt) => onPickDirectPrice(0, opt)}
                    styles={adminToolbarSelect}
                    isSearchable
                    isLoading={directProductsLoading}
                    placeholder={directProductsLoading ? 'Loading plans…' : 'Search allowlisted plans…'}
                    noOptionsMessage={() =>
                      `No allowlisted plans for ${form.billingCurrency}. This is the environment catalog for that currency, not a per-consultant list.`
                    }
                  />
                </div>
                {!directProductsLoading && directPriceOptions.length === 0 && (
                  <Alert variant='warning'>
                    <AlertDescription className='text-xs text-foreground'>
                      No subscription plans are allowlisted for {form.billingCurrency} on this environment. Try another
                      billing currency, or use &quot;Billed to you&quot;. If GBP/USD are also empty, this is a catalog
                      setup issue for all consultant admins — not just a test organisation.
                    </AlertDescription>
                  </Alert>
                )}
                {directPicked && isTieredPrice(directPicked.price) && (
                  <div>
                    <FieldHelp
                      label={`${directBillingUnit?.label || 'Banding quantity'} *`}
                      help={bandingHelp}
                    />
                    <Input
                      type='number'
                      min='1'
                      value={directLine.bandingQuantity}
                      onChange={(e) => setLine(0, 'bandingQuantity', e.target.value)}
                    />
                    {directLine.bandingQuantity && (
                      <p className='text-xs text-muted-foreground mt-1'>
                        Matched tier:{' '}
                        {formatMajorMoney(
                          directPicked.price.currency,
                          typeof matchedDirectTierMinor === 'number' ? matchedDirectTierMinor / 100 : null,
                          stripeCurrencyToSymbol,
                        )}
                      </p>
                    )}
                  </div>
                )}
                <div>
                  <Label>Client pay mode</Label>
                  <Select
                    value={form.directPayMode}
                    onValueChange={(v) => update('directPayMode', v)}
                    options={[
                      { value: 'invoice', label: 'Invoice' },
                      { value: 'checkout', label: 'Card checkout' },
                      { value: 'both', label: 'Invoice or checkout' },
                    ]}
                  />
                </div>
                <div className='grid grid-cols-2 gap-3'>
                  <div>
                    <FieldHelp
                      label='Term length (months)'
                      help='How long the client is committed to this plan before they can leave at the end of a term. Default 12 months.'
                    />
                    <Input
                      type='number'
                      min='1'
                      value={form.term.lengthMonths}
                      onChange={(e) => update('term', { ...form.term, lengthMonths: e.target.value })}
                    />
                  </div>
                  <div>
                    <FieldHelp
                      label='Notice period (days)'
                      help='How many days before the term end the client must give notice if they do not want to auto-renew. Default 60 days.'
                    />
                    <Input
                      type='number'
                      min='0'
                      value={form.term.noticePeriodDays}
                      onChange={(e) => update('term', { ...form.term, noticePeriodDays: e.target.value })}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        <DialogFooter className='gap-2'>
          <Button variant='outline' onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          {step > 1 && (
            <Button variant='outline' onClick={() => setStep(1)} disabled={saving}>
              Back
            </Button>
          )}
          {step === 1 ? (
            <Button onClick={() => setStep(2)} disabled={isDirect && directFlowDisabled}>
              Continue
            </Button>
          ) : (
            <Button
              loading={saving}
              onClick={onSubmit}
              disabled={isDirect && (directProductsLoading || directPriceOptions.length === 0)}>
              Create client
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const mapStateToProps = (state) => ({
  countries: state.organisation?.countries,
  consultantParentId: state.profile?.details?.consultantAdmin,
  directProducts: state.consultantClients?.directProducts,
  directProductsLoading: !!state.consultantClients?.loading?.directProducts,
  agreementProductsByCurrency: state.agreements?.products?.byCurrency || {},
});

const mapDispatchToProps = (dispatch) => ({
  createConsultantClient: (body) => dispatch(CONSULTANT_ACTIONS.createConsultantClient(body)),
  getDirectSubscriptionProducts: (currency) => dispatch(CONSULTANT_ACTIONS.getDirectSubscriptionProducts(currency)),
  getStripeProducts: (params) => dispatch(AGREEMENTS.getStripeProducts(params)),
  getMyAgreements: (organisationId) => dispatch(AGREEMENTS.getMyAgreements(organisationId)),
  showAlert: (status, message, severity, hideDuration) =>
    dispatch(toggleAlertMessage(status, message, severity, hideDuration)),
});

export default connect(mapStateToProps, mapDispatchToProps)(AddConsultantClientWizard);
